import ModuleTranslations from "../Conditions/ModuleTranslations";
import { getTranslatedDefinition } from "../utils";
import { messages, tickerValidator } from "../../globalVariables";
import { BASE_URL } from "../../request";
import { PAYMENT_TYPES_NAMES } from "../../Common/constants";
import { FormattedMessage } from "react-intl";
import React from "react";

/**
 * Created by mongoose on 8/11/17.
 */
const ModuleDefinition = {
  name: "Conditions",
  entityName: "conditions",
  restConfig: {
    baseURL: `${BASE_URL}`,
    create: {
      url: ({ propertyTicker }) => `api/backoffice/${propertyTicker}/conditions`,
      method: "post",
      resetCache: false,
    },
    update: {
      url: ({ propertyTicker }) => `api/backoffice/${propertyTicker}/conditions`,
      method: "put",
      resetCache: false,
    },
    fetch: {
      url: ({ propertyTicker, ticker }) =>
        `api/backoffice/${propertyTicker}/conditions/${ticker ? ticker : ""}`,
      method: "get",
    },
    delete: {
      url: ({ propertyTicker, ticker }) => `api/backoffice/${propertyTicker}/conditions/${ticker}`,
      method: "delete",
      resetCache: false,
    },
  },
  entities: [],
  allowClone: true,
  renderers: {
    list: {},
    form: {},
  }, //render functions by attribute name, this overrides default widgets specified in the fields section
  translation: {
    translated: true,
    fieldName: "translations",
    displayName: "translations",
    widget: "translations",
    entityProperty: "translations",
    hasAdvancedTranslations: true,
  },
  relatedEntities: [
    {
      name: "cancellationPolicy",
    },
    {
      name: "paymentTypes",
    },
  ],
  fields: {
    list: [
      //columns to be used in the table/list display, Order IS important
      {
        fieldName: "ticker",
        displayName: "Ticker",
      },
      {
        fieldName: "name",
        displayName: "Name",
      },
      {
        fieldName: "conditionType",
        displayName: "Condition type",
        widget: "custom",
        // Fallback labels for locales whose list ModuleTranslations don't override optionDisplay
        // (e.g. the default es list column). en/fr provide their own localized labels.
        optionDisplay: {
          ENGINE: { label: "Motor" },
          STORE: { label: "Tienda" },
          FLIGHT_HOTEL: { label: "Vuelo + Hotel" },
        },
        // One column for the whole enum. The row keeps the raw enum (ConditionsList does not coerce
        // it), and the label comes from optionDisplay, which ModuleTranslations overrides per locale.
        customRenderer: ({ field, entity }) => (
          <>
            {(field.optionDisplay[entity.conditionType] || {}).label || entity.conditionType || ""}
          </>
        ),
      },
      {
        fieldName: "earlyCharge",
        displayName: "%deposit",
      },
      {
        fieldName: "chargeFirstNight",
        displayName: "Charge first night",
      },
      {
        fieldName: "policies",
        displayName: "Cancellation policies",
        widget: "arrayRenderer",
      },
      {
        fieldName: "payments",
        displayName: "Payment methods",
        widget: "arrayRenderer",
      },
      {
        fieldName: "minimumCharge",
        displayName: "Minimum charge",
      },
      {
        fieldName: "motorsList",
        displayName: "Motors",
      },
      {
        fieldName: "uso",
        displayName: "Usage",
      },
      // color column REMOVED (HU5/HU7)
    ],
    form: [
      //fields to be used in the creation/edit form display, Order IS important
      {
        fieldName: "id",
        widget: "hidden",
      },
      {
        fieldName: "propertyTicker",
        widget: "hidden",
      },
      {
        fieldName: "code",
        widget: "hidden",
      },
      {
        fieldName: "type",
        widget: "hidden",
      },
      {
        fieldName: "ordering",
        widget: "hidden",
        defaultValue: 1,
      },
      {
        fieldName: "ticker",
        displayName: "Ticker",
        className: "tickerInput",
        widget: "input",
        readOnly: true,
        required: true,
      },
      {
        fieldName: "conditionType",
        displayName: "Store condition",
        helpText:
          "Una condición no puede ser de tienda y de Vuelo + Hotel a la vez: " +
          "al activar una se desactiva la otra.",
        widget: "singleSwitch",
        visible: () => false, // overridden in ConditionsForm computedFields
      },
      {
        fieldName: "isFlightHotelCondition",
        displayName: "Flight + Hotel condition",
        helpText:
          "Una condición no puede ser de tienda y de Vuelo + Hotel a la vez: " +
          "al activar una se desactiva la otra.",
        widget: "singleSwitch",
        defaultValue: false,
        visible: () => false, // overridden in ConditionsForm computedFields
      },
      {
        fieldName: "flightHotelPaymentType",
        displayName: "Prepayment (Maarlab)",
        helpText:
          "Qué cobra Maarlab en el momento de la reserva. " +
          "Solo vuelo: se cobra únicamente el vuelo. " +
          "Todo ahora: vuelo + hotel.",
        // Temporarily disabled with the FLIGHT_NOW_HOTEL_LATER option below.
        // "Vuelo ahora / hotel después: el hotel se cobra en la fecha diferida.",
        widget: "selector",
        optionDisplay: {
          FLIGHT_ONLY: { label: "Solo vuelo" },
          ALL_NOW: { label: "Todo ahora" },
          // Temporarily disabled: FLIGHT_NOW_HOTEL_LATER is not offered for now.
          // FLIGHT_NOW_HOTEL_LATER: { label: "Vuelo ahora / hotel después" },
        },
        defaultValue: "FLIGHT_ONLY",
        visible: () => false, // overridden in ConditionsForm computedFields
      },
      {
        translationsList: true,
      },
      {
        fieldName: "name",
        displayName: "Name",
        widget: "input",
        translated: true,
        required: true,
      },
      {
        fieldName: "htmlCancellation",
        displayName: "Cancellations / Prepayment",
        widget: "richHTMLEditor",
        translated: true,
        required: true,
      },
      {
        fieldName: "htmlCheckIn",
        displayName: "Check-in time",
        widget: "richHTMLEditor",
        translated: true,
        advancedTranslation: true,
        visible: ({ showAdvancedTranslations }) => showAdvancedTranslations,
      },
      {
        fieldName: "htmlCheckOut",
        displayName: "Check-out time",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ showAdvancedTranslations }) => showAdvancedTranslations,
      },
      {
        fieldName: "htmlChildren",
        displayName: "Children and extra beds",
        widget: "richHTMLEditor",
        translated: true,
        advancedTranslation: true,
        visible: ({ showAdvancedTranslations }) => showAdvancedTranslations,
      },
      {
        fieldName: "htmlPets",
        displayName: "Pets",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ showAdvancedTranslations }) => showAdvancedTranslations,
      },
      {
        fieldName: "htmlGroups",
        displayName: "Groups",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ showAdvancedTranslations }) => showAdvancedTranslations,
      },
      {
        fieldName: "htmlExtraInfo",
        displayName: "More information",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ showAdvancedTranslations }) => showAdvancedTranslations,
      },
      {
        fieldName: "color",
        displayName: "Color",
        widget: "colorPicker",
        defaultValue: "#908f96",
      },
      {
        fieldName: "payments",
        displayName: "Payment methods",
        defaultValue: [],
        selectables: true,
        selectAllText: "Select all",
        selectNoneText: "Select none",
        widget: "multicheckbox",
        multiCheckboxType: "LIST",
        entity: "paymentTypes",
        entityDisplay: "type",
        entitySecondaryDisplay: "title",
        entitySecondaryDisplayTranslated: true,
        entityKey: "type",
        fetch: true,
        required: true,
        mapping: {
          es: {
            CREDIT_CARD_VAULT: "Tarjeta como garantía",
            POS: "TPV",
            TRANSFER: "Transferencia",
            WITHOUT_CARD: "Reserva sin tarjeta",
            RESERVATION_REQUEST: "Solicitud de reserva",
            PNP_TOKEN: "Pago Tokenizado PNP",
            PAY_NO_PAIN_V2: PAYMENT_TYPES_NAMES.es.PAY_NO_PAIN_V2,
            SIPAY_TOKENIZED_VAULT: "Pago Tokenizado PNP",
            SIPAY_TOKENIZED_POS: "Pago Tokenizado PNP",
            PAYPAL_EXPRESS_CHECKOUT: "PayPal",
            PAYPAL_EXPRESS_STANDARD: "PayPal",
            STRIPE: "Stripe",
            PCI_PROXY: "PCI Proxy",
            ADDON_PAYMENTS: "Addon payment",
            DEFERRED: "Pago diferido",
            CRIPTAN: "Criptan",
            PAY_NO_PAIN_PROXY: "TC Garantía (PCI)",
            NEMURU: "Nemuru",
            SEPTEO_PAYMENTS: PAYMENT_TYPES_NAMES.es.SEPTEO_PAYMENTS,
            SIPAY: PAYMENT_TYPES_NAMES.es.SIPAY,
          },
          en: {
            CREDIT_CARD_VAULT: "Credit Card as a Guarantee",
            POS: "TPV",
            TRANSFER: "Wire Transfer",
            WITHOUT_CARD: "No credit card needed",
            RESERVATION_REQUEST: "Booking request",
            PNP_TOKEN: "Tokenized payment PNP",
            PAY_NO_PAIN_V2: PAYMENT_TYPES_NAMES.en.PAY_NO_PAIN_V2,
            SIPAY_TOKENIZED_VAULT: "Tokenized payment PNP",
            SIPAY_TOKENIZED_POS: "Tokenized payment PNP",
            PAYPAL_EXPRESS_CHECKOUT: "PayPal",
            PAYPAL_EXPRESS_STANDARD: "PayPal",
            STRIPE: "Stripe",
            PCI_PROXY: "PCI Proxy",
            ADDON_PAYMENTS: "Addon payment",
            DEFERRED: "Deferred payment",
            CRIPTAN: "Criptan",
            PAY_NO_PAIN_PROXY: "CC Guarantee (PCI)",
            NEMURU: "Nemuru",
            SEPTEO_PAYMENTS: PAYMENT_TYPES_NAMES.en.SEPTEO_PAYMENTS,
            SIPAY: PAYMENT_TYPES_NAMES.en.SIPAY,
          },
          fr: {
            CREDIT_CARD_VAULT: PAYMENT_TYPES_NAMES.fr.CREDIT_CARD_VAULT,
            POS: PAYMENT_TYPES_NAMES.fr.POS,
            TRANSFER: PAYMENT_TYPES_NAMES.fr.TRANSFER,
            WITHOUT_CARD: PAYMENT_TYPES_NAMES.fr.WITHOUT_CARD,
            RESERVATION_REQUEST: PAYMENT_TYPES_NAMES.fr.RESERVATION_REQUEST,
            PNP_TOKEN: PAYMENT_TYPES_NAMES.fr.PNP_TOKEN,
            PAY_NO_PAIN_V2: PAYMENT_TYPES_NAMES.fr.PAY_NO_PAIN_V2,
            SIPAY_TOKENIZED_VAULT: PAYMENT_TYPES_NAMES.fr.SIPAY_TOKENIZED_VAULT,
            SIPAY_TOKENIZED_POS: PAYMENT_TYPES_NAMES.fr.SIPAY_TOKENIZED_POS,
            PAYPAL_EXPRESS_CHECKOUT: PAYMENT_TYPES_NAMES.fr.PAYPAL_EXPRESS_CHECKOUT,
            PAYPAL_EXPRESS_STANDARD: PAYMENT_TYPES_NAMES.fr.PAYPAL_EXPRESS_STANDARD,
            STRIPE: PAYMENT_TYPES_NAMES.fr.STRIPE,
            PCI_PROXY: PAYMENT_TYPES_NAMES.fr.PCI_PROXY,
            ADDON_PAYMENTS: PAYMENT_TYPES_NAMES.fr.ADDON_PAYMENTS,
            DEFERRED: PAYMENT_TYPES_NAMES.fr.DEFERRED,
            CRIPTAN: PAYMENT_TYPES_NAMES.fr.CRIPTAN,
            PAY_NO_PAIN_PROXY: PAYMENT_TYPES_NAMES.fr.PAY_NO_PAIN_PROXY,
            NEMURU: PAYMENT_TYPES_NAMES.fr.NEMURU,
            SEPTEO_PAYMENTS: PAYMENT_TYPES_NAMES.fr.SEPTEO_PAYMENTS,
            SIPAY: PAYMENT_TYPES_NAMES.fr.SIPAY,
          },
        },
      },
      {
        fieldName: "earlyCharge",
        displayName: "% Advance deposit",
        widget: "input",
        defaultValue: 0,
        required: true,
      },
      {
        fieldName: "minimumCharge",
        displayName: "Minimum charge",
        widget: "input",
        defaultValue: 0,
        required: true,
      },
      {
        fieldName: "chargeFirstNight",
        displayName: "Charge first night",
        widget: "singleSwitch",
        defaultValue: false,
      },
      {
        fieldName: "policies",
        displayName: "Cancellation policies",
        defaultValue: [],
        selectables: true,
        selectAllText: "Select all",
        selectNoneText: "Select none",
        widget: "multicheckbox",
        entity: "cancellationPolicy",
        entityDisplay: "ticker",
        entityKey: "ticker",
        fetch: true,
        required: true,
      },
      // ── AGR-only: property selector ─────────────────────────────────────────────
      // Visible only when _isChain is injected into formValues by ConditionsForm mapper.
      {
        fieldName: "selectedMotorsSection",
        displayName: "Property selection",
        widget: "section",
        visible: ({ formValues }) => !!formValues._isChain,
      },
      {
        fieldName: "selectedMotors",
        displayName: "",
        widget: "dualListBox",
        defaultValue: [],
        // transform injects dynamic options + headers using chain metadata from formValues
        transform: ({ field, formValues }) => {
          const allChildren = formValues._chainChildren || [];
          const selected = formValues.selectedMotors || [];
          const availableCount = Math.max(0, allChildren.length - selected.length);
          const availableLabel =
            typeof formValues._availableLabel === "function"
              ? formValues._availableLabel(availableCount)
              : `Available (${availableCount})`;
          const selectedLabel =
            typeof formValues._selectedLabel === "function"
              ? formValues._selectedLabel(selected.length)
              : `Selected (${selected.length})`;
          return {
            ...field,
            options: allChildren.map(ticker => ({
              label: ticker,
              value: ticker,
            })),
            leftListHeader: availableLabel,
            rightListHeader: selectedLabel,
          };
        },
        visible: ({ formValues }) => !!formValues._isChain,
      },
    ],
  },
  validation: {
    ticker: {
      ...tickerValidator,
      constraints: {
        ...tickerValidator.constraints,
        length: { maximum: 25, message: <FormattedMessage {...messages.tickerLengthValidation} /> },
      },
    },
    propertyTicker: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    color: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    payments: {
      type: "Array",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    earlyCharge: {
      type: "Number",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    minimumCharge: {
      type: "Number",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    chargeFirstNight: {},
    ordering: {
      type: "Number",
      constraints: {
        presence: {
          allowEmpty: true,
        },
      },
    },
    policies: {
      type: "Array",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    name: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    htmlCancellation: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
  },
};

export default ({ locale }) =>
  getTranslatedDefinition({ ModuleDefinition, ModuleTranslations, locale });
